import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaInstagram, FaWhatsapp, FaMapMarkerAlt, FaClock, FaHeart, FaArrowUp } from 'react-icons/fa';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInstagramClick = () => {
    window.open('https://www.instagram.com/m30fitness/', '_blank');
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/5531971934978', '_blank');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FooterContainer id="contato" ref={footerRef}>
      <BackgroundGlow />
      
      <FooterContent $isVisible={isVisible}>
        <TopSection>
          <BrandSection>
            <Logo>
              <LogoText>M30</LogoText>
              <LogoAccent>FITNESS</LogoAccent>
            </Logo>
            <BrandDescription>
              Transformando vidas através do esporte há mais de 10 anos. 
              Sua jornada para uma vida mais saudável começa aqui.
            </BrandDescription>
            <SocialLinks>
              <SocialButton onClick={handleInstagramClick} $color="#E1306C">
                <FaInstagram />
              </SocialButton>
              <SocialButton onClick={handleWhatsAppClick} $color="#25D366">
                <FaWhatsapp />
              </SocialButton>
            </SocialLinks>
          </BrandSection>

          <InfoSection>
            <InfoCard>
              <InfoIcon>
                <FaMapMarkerAlt />
              </InfoIcon>
              <InfoContent>
                <InfoTitle>Localização</InfoTitle>
                <InfoText>Av. Otawa, 257</InfoText>
                <InfoText>Jardim Canadá</InfoText>
              </InfoContent>
            </InfoCard>

            <InfoCard>
              <InfoIcon>
                <FaClock />
              </InfoIcon>
              <InfoContent>
                <InfoTitle>Horário de Funcionamento</InfoTitle>
                <InfoText>Segunda a Sexta: 6h às 22h</InfoText>
                <InfoText>Sábado: 8h às 12h</InfoText>
                <InfoTextSmall>Domingos e feriados: Fechado</InfoTextSmall>
              </InfoContent>
            </InfoCard>

            <InfoCard>
              <InfoIcon>
                <FaWhatsapp />
              </InfoIcon>
              <InfoContent>
                <InfoTitle>WhatsApp</InfoTitle>
                <InfoText>(31) 97193-4978</InfoText>
                <CTALink href="https://wa.me/5531971934978" target="_blank">
                  Fale Conosco →
                </CTALink>
              </InfoContent>
            </InfoCard>
          </InfoSection>
        </TopSection>

        <Divider />

        <BottomSection>
          <Copyright>
            <span>© {new Date().getFullYear()} M30 Fitness. Todos os direitos reservados.</span>
            <MadeWith>
              Feito com <HeartIcon><FaHeart /></HeartIcon> para sua saúde
            </MadeWith>
          </Copyright>
          
          <ScrollTopButton onClick={scrollToTop}>
            <FaArrowUp />
          </ScrollTopButton>
        </BottomSection>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const FooterContainer = styled.footer`
  width: 100%;
  background: linear-gradient(180deg, var(--bg-dark) 0%, #080808 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 100%;
  height: 200px;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, var(--primary-glow) 0%, transparent 100%);
  opacity: 0.05;
  pointer-events: none;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 30px 30px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease forwards;
  `}
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 60px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 50px;
  }
`;

const BrandSection = styled.div`
  flex: 1;
  max-width: 350px;

  @media (max-width: 900px) {
    max-width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 20px;
`;

const LogoText = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: 2px;
`;

const LogoAccent = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-left: 5px;
  letter-spacing: 3px;
`;

const BrandDescription = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1rem;
  color: #666;
  line-height: 1.8;
  margin-bottom: 25px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
`;

const SocialButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #fff;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $color }) => $color};
    border-color: ${({ $color }) => $color};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px ${({ $color }) => `${$color}40`};
  }
`;

const InfoSection = styled.div`
  flex: 2;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const InfoCard = styled.div`
  display: flex;
  gap: 15px;
  padding: 25px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(229, 168, 0, 0.3);
    transform: translateY(-5px);
  }

  @media (max-width: 900px) {
    justify-content: center;
    text-align: center;
    flex-direction: column;
    align-items: center;
  }
`;

const InfoIcon = styled.div`
  width: 45px;
  height: 45px;
  background: rgba(229, 168, 0, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoTitle = styled.h4`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 8px;
`;

const InfoText = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1rem;
  color: #ccc;
  line-height: 1.6;
`;

const InfoTextSmall = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.85rem;
  color: #666;
  margin-top: 5px;
`;

const CTALink = styled.a`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  margin-top: 10px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    text-shadow: 0 0 10px var(--primary-glow);
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  margin: 50px 0 30px;
`;

const BottomSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Copyright = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-size: 0.9rem;
    color: #555;
  }

  @media (max-width: 600px) {
    text-align: center;
  }
`;

const MadeWith = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: flex-start;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const HeartIcon = styled.span`
  color: #ff4757;
  animation: ${pulse} 1s ease-in-out infinite;
  display: flex;
  align-items: center;
`;

const ScrollTopButton = styled.button`
  width: 50px;
  height: 50px;
  background: var(--gradient-primary);
  border: none;
  border-radius: 50%;
  color: #000;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${float} 3s ease-in-out infinite;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px var(--primary-glow);
  }
`;
